/**
 * ReleaseNotesPanel — cross-service release-notes card for the drift tab.
 *
 * Rendered inside DriftTab above the per-service rows when the user has
 * clicked the "Release Notes" button and the reducer response has loaded.
 * Sub-task CORE-1011 of the CORE-1003 release-notes reducer epic.
 *
 * Panel layout — three visually distinct blocks inside one card, matching
 * the CORE-987 SummarisePanel card pattern:
 *
 *   1. **Release Notes** — `combined_narrative` paragraph + optional
 *      grouped contract-impact sections (frontend↔backend, service↔service,
 *      db schema).
 *   2. **Attention needed** — prioritised list of `attention_needed`
 *      items. Each item's involved services render as clickable chips
 *      that scroll to the target DriftRow and auto-trigger its Summarise
 *      button (`onServiceClick` prop, wired by DriftTab).
 *   3. **Cost** — `estimated_cost_usd` (reducer) + total including
 *      per-service costs + `generated_at` + refresh button (1h-disabled
 *      per CORE-987) + a "Show per-service breakdown" toggle expanding
 *      `per_service_cost_breakdown`.
 *
 * Stale banner: if any per-service summary was refreshed AFTER the
 * release-notes response was generated, show a banner prompting refresh.
 * Detection is done in DriftTab and passed as `stale` boolean — this
 * component just renders the banner conditionally so the ownership of
 * the timestamp comparison stays with the caller that owns the state.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  AttentionItem,
  ContractGrouping,
  PerServiceCostBreakdown,
  ReleaseNotesResponse,
} from '@/types/api';
import { formatCurrency, formatRelativeAge } from '@/utils/format';

/**
 * Cache TTL — mirrors the release-notes S3 cache in the Lambda handler
 * (observability-api/src/lambdas/drift-summary/releaseNotesHandler.ts).
 * Same value + coordination risk as SummarisePanel's
 * SUMMARY_CACHE_TTL_MS — if the Lambda TTL changes, this must move in
 * lockstep. Future ticket: transmit `cache_ttl_seconds` in the envelope.
 */
const RELEASE_NOTES_CACHE_TTL_MS = 60 * 60 * 1000;

interface ReleaseNotesPanelProps {
  readonly response: ReleaseNotesResponse;
  /** Click Refresh → re-fetches. Disabled by the panel until TTL elapses. */
  readonly onRefresh: () => void;
  /**
   * Click an attention-item service chip → DriftTab scrolls the target
   * row into view, auto-expands it, and (if the row's summary isn't
   * loaded yet) fires its Summarise fetch. Chip clicks pass the exact
   * service name; DriftTab looks up the row from there.
   */
  readonly onServiceClick: (service: string) => void;
  /**
   * True when at least one per-service summary was refreshed AFTER
   * `response.generated_at`. DriftTab owns the timestamp comparison
   * because it owns the per-service state; this component just renders
   * the banner. Defaults to false so most tests / callers ignore it.
   */
  readonly stale?: boolean;
  /**
   * Injectable clock — tests pass a fixed date so cached-age rendering
   * and refresh-countdown assertions are deterministic. Production
   * callers omit and get real wall-clock (via DriftTab's own clock).
   */
  readonly now?: Date;
}

export function ReleaseNotesPanel({
  response,
  onRefresh,
  onServiceClick,
  stale = false,
  now,
}: ReleaseNotesPanelProps): JSX.Element {
  const effectiveNow = now ?? new Date();
  const totalCost = computeTotalCost(response);
  return (
    <div className="release-notes-panel" data-testid="release-notes-panel">
      {stale && (
        <div className="release-notes-stale-banner" role="alert" data-testid="release-notes-stale">
          A per-service summary was refreshed after these release notes were generated. Refresh to
          incorporate.
        </div>
      )}
      <NarrativeBlock response={response} />
      <AttentionBlock items={response.attention_needed} onServiceClick={onServiceClick} />
      <CostBlock
        response={response}
        totalCost={totalCost}
        now={effectiveNow}
        onRefresh={onRefresh}
      />
    </div>
  );
}

// --- Narrative block --------------------------------------------------------

function NarrativeBlock({ response }: { readonly response: ReleaseNotesResponse }): JSX.Element {
  return (
    <section
      className="release-notes-block release-notes-block-narrative"
      data-testid="release-notes-block-narrative"
    >
      <h4 className="release-notes-block-heading">Release Notes</h4>
      <p className="release-notes-narrative">{response.combined_narrative}</p>
      <ContractGroupingsView groupings={response.contract_groupings} />
    </section>
  );
}

const CONTRACT_GROUPING_LABELS: Readonly<
  Record<keyof ReleaseNotesResponse['contract_groupings'], string>
> = {
  frontend_backend: 'Frontend ↔ backend',
  service_to_service: 'Service ↔ service',
  db_schema: 'Database schema',
};

function ContractGroupingsView({
  groupings,
}: {
  readonly groupings: ReleaseNotesResponse['contract_groupings'];
}): JSX.Element | null {
  const nonEmpty = (
    Object.entries(groupings) as ReadonlyArray<
      [keyof ReleaseNotesResponse['contract_groupings'], readonly ContractGrouping[]]
    >
  ).filter(([, items]) => items.length > 0);
  if (nonEmpty.length === 0) return null;
  return (
    <div className="release-notes-groupings" data-testid="release-notes-groupings">
      {nonEmpty.map(([boundary, items]) => (
        <div key={boundary} className="release-notes-grouping">
          <h5 className="release-notes-grouping-heading">{CONTRACT_GROUPING_LABELS[boundary]}</h5>
          <ul className="release-notes-grouping-list">
            {items.map((item, idx) => (
              <li key={`${boundary}-${idx}`} className="release-notes-grouping-item">
                <p className="release-notes-grouping-description">{item.description}</p>
                {item.services.length > 0 && (
                  <p className="release-notes-grouping-services">{item.services.join(', ')}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// --- Attention block --------------------------------------------------------

function AttentionBlock({
  items,
  onServiceClick,
}: {
  readonly items: readonly AttentionItem[];
  readonly onServiceClick: (service: string) => void;
}): JSX.Element {
  if (items.length === 0) {
    return (
      <section
        className="release-notes-block release-notes-block-attention"
        data-testid="release-notes-block-attention"
      >
        <h4 className="release-notes-block-heading">Attention needed</h4>
        <p className="release-notes-attention-empty">No cross-service concerns flagged.</p>
      </section>
    );
  }
  return (
    <section
      className="release-notes-block release-notes-block-attention release-notes-attention-alert"
      data-testid="release-notes-block-attention"
    >
      <h4 className="release-notes-block-heading">Attention needed</h4>
      <ol className="release-notes-attention-list">
        {items.map((item, idx) => (
          <li key={`${item.title}-${idx}`} className="release-notes-attention-item">
            <p className="release-notes-attention-title">{item.title}</p>
            {item.services.length > 0 && (
              <div className="release-notes-attention-services" data-testid="release-notes-attention-services">
                {item.services.map((service) => (
                  <button
                    key={service}
                    type="button"
                    className="release-notes-service-chip"
                    onClick={(): void => onServiceClick(service)}
                    aria-label={`Open ${service} summary`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            )}
            <p className="release-notes-attention-detail">{item.detail}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// --- Cost block -------------------------------------------------------------

function CostBlock({
  response,
  totalCost,
  now,
  onRefresh,
}: {
  readonly response: ReleaseNotesResponse;
  readonly totalCost: number;
  readonly now: Date;
  readonly onRefresh: () => void;
}): JSX.Element {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const cachedFlag = response.served_from_cache ? ' • served from cache' : '';
  const age = formatRelativeAge(response.generated_at, now);
  const model = response.pricing_model_id;
  return (
    <section
      className="release-notes-block release-notes-block-cost"
      data-testid="release-notes-block-cost"
    >
      <h4 className="release-notes-block-heading">Cost</h4>
      <p className="release-notes-cost-line" data-testid="release-notes-cost-line">
        Total {formatCurrency(totalCost, 'USD')} ({formatCurrency(response.estimated_cost_usd, 'USD')}{' '}
        reducer + {formatCurrency(totalCost - response.estimated_cost_usd, 'USD')} per-service) •{' '}
        {model}{cachedFlag}
      </p>
      <p className="release-notes-generated-at" data-testid="release-notes-generated-at">
        Generated {age}
      </p>
      <RefreshControl
        generatedAt={response.generated_at}
        now={now}
        onRefresh={onRefresh}
      />
      <button
        type="button"
        className="release-notes-breakdown-toggle"
        onClick={(): void => setBreakdownOpen((prev) => !prev)}
        aria-expanded={breakdownOpen}
        aria-controls="release-notes-per-service-breakdown"
      >
        {breakdownOpen ? 'Hide' : 'Show'} per-service breakdown
      </button>
      {breakdownOpen && (
        <PerServiceBreakdown
          id="release-notes-per-service-breakdown"
          breakdown={response.per_service_cost_breakdown}
        />
      )}
    </section>
  );
}

function PerServiceBreakdown({
  id,
  breakdown,
}: {
  readonly id: string;
  readonly breakdown: Readonly<Record<string, PerServiceCostBreakdown>>;
}): JSX.Element {
  const entries = Object.entries(breakdown).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <p id={id} className="release-notes-breakdown-empty" data-testid="release-notes-breakdown">
        No per-service costs to display.
      </p>
    );
  }
  return (
    <ul id={id} className="release-notes-breakdown" data-testid="release-notes-breakdown">
      {entries.map(([service, cost]) => (
        <li key={service} className="release-notes-breakdown-item">
          <span className="release-notes-breakdown-service">{service}</span>
          <span className="release-notes-breakdown-cost">
            {formatCurrency(cost.estimated_cost_usd, 'USD')}
            {cost.served_from_cache && (
              <span className="release-notes-breakdown-cached" aria-label="served from cache">
                {' '}
                (cached)
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

// --- Refresh + confirm modal (shared with DriftTab) -------------------------

function RefreshControl({
  generatedAt,
  now,
  onRefresh,
}: {
  readonly generatedAt: string;
  readonly now: Date;
  readonly onRefresh: () => void;
}): JSX.Element {
  const canRefresh = cacheHasExpired(generatedAt, now);
  const countdown = canRefresh ? null : timeUntilCacheExpires(generatedAt, now);
  return (
    <div className="release-notes-refresh-row">
      <button
        type="button"
        className="release-notes-refresh-btn"
        disabled={!canRefresh}
        onClick={onRefresh}
        title={canRefresh ? 'Regenerate release notes' : `Available in ${countdown}`}
      >
        Refresh
      </button>
      {!canRefresh && countdown !== null && (
        <span className="release-notes-refresh-countdown" data-testid="release-notes-refresh-countdown">
          Available in {countdown}
        </span>
      )}
    </div>
  );
}

/**
 * Total cost = reducer estimated_cost_usd + Σ per-service estimated_cost_usd.
 * Exported for the DriftTab confirm modal so the estimate we quote pre-click
 * matches the total we display post-load — no divergent formulas.
 */
export function computeTotalCost(response: ReleaseNotesResponse): number {
  const perServiceSum = Object.values(response.per_service_cost_breakdown).reduce(
    (sum, cost) => sum + cost.estimated_cost_usd,
    0,
  );
  return response.estimated_cost_usd + perServiceSum;
}

function cacheHasExpired(generatedAt: string, now: Date): boolean {
  const generatedMs = new Date(generatedAt).getTime();
  if (Number.isNaN(generatedMs)) return true;
  return now.getTime() - generatedMs >= RELEASE_NOTES_CACHE_TTL_MS;
}

function timeUntilCacheExpires(generatedAt: string, now: Date): string | null {
  const generatedMs = new Date(generatedAt).getTime();
  if (Number.isNaN(generatedMs)) return null;
  const rawRemaining = RELEASE_NOTES_CACHE_TTL_MS - (now.getTime() - generatedMs);
  if (rawRemaining <= 0) return null;
  const msRemaining = Math.min(rawRemaining, RELEASE_NOTES_CACHE_TTL_MS);
  const minutes = Math.ceil(msRemaining / 60_000);
  return `${minutes}m`;
}

// --- Confirm modal ----------------------------------------------------------

/**
 * Cost estimate constants for the pre-click confirm modal (CORE-1011).
 * Values derive from the drift-summary per-service Claude call cost and
 * the reducer cost observed in dev — both are Sonnet 4.5 calls, so these
 * are ballpark averages. The real cost is displayed post-load in the
 * Cost block; the confirm modal is a "roughly what this will cost" hint,
 * not a precise quote.
 *
 * Kept in sync with the values documented in the CORE-1011 ticket. If
 * the Sonnet 4.5 pricing changes materially, update both here and the
 * ticket's example.
 */
export const PER_SERVICE_COLD_COST_USD = 0.02;
export const REDUCER_COLD_COST_USD = 0.005;

export interface CostEstimate {
  readonly total: number;
  readonly cachedCount: number;
  readonly coldCount: number;
}

/**
 * Compute the pre-click cost estimate for the confirm modal.
 *
 * The estimate splits `services` into cached (via `summary_cached`) and
 * cold, applies the ballpark per-service constants, and adds the reducer
 * ballpark. The reducer cost is ALWAYS incurred on a cache-miss release-
 * notes call — even when all per-service summaries are cached, the reducer
 * still runs (it's how we build the combined narrative from the cached
 * inputs). If the release-notes cache itself is warm the entire call is
 * $0, but the confirm modal only fires when the user has explicitly asked
 * for a fresh call, so we don't account for that path here.
 */
export function computeCostEstimate(
  services: ReadonlyArray<{ readonly summary_cached: boolean }>,
): CostEstimate {
  let cachedCount = 0;
  let coldCount = 0;
  for (const s of services) {
    if (s.summary_cached) cachedCount++;
    else coldCount++;
  }
  const total = coldCount * PER_SERVICE_COLD_COST_USD + REDUCER_COLD_COST_USD;
  return { total, cachedCount, coldCount };
}

interface ReleaseNotesConfirmModalProps {
  readonly from: string;
  readonly to: string;
  readonly estimate: CostEstimate;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

/**
 * Confirm modal shown before firing the release-notes fetch. Renders a
 * cost estimate derived from the already-loaded /drift response's
 * `summary_cached` flags — no server round-trip needed to price the
 * click.
 *
 * The modal renders a semi-transparent full-screen overlay with the
 * dialog centered. Keyboard interactions:
 *   - Focus trap kept simple: initial focus lands on Cancel (safer
 *     default than Continue for an action that costs money).
 *   - Escape key = Cancel (via the overlay's onKeyDown handler).
 *   - Click on the overlay backdrop = Cancel.
 */
export function ReleaseNotesConfirmModal({
  from,
  to,
  estimate,
  onCancel,
  onConfirm,
}: ReleaseNotesConfirmModalProps): JSX.Element {
  const totalServices = estimate.cachedCount + estimate.coldCount;
  // Focus Cancel on mount — safer default for a paid action than Continue.
  // Using a ref + useEffect rather than autoFocus so ESLint's a11y rules
  // (which flag autoFocus) stay satisfied.
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  return (
    <div
      className="release-notes-modal-overlay"
      data-testid="release-notes-confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="release-notes-confirm-title"
      onClick={(e): void => {
        // Only cancel if the click landed on the backdrop itself, not
        // inside the dialog card. Bubbled clicks from Continue would
        // otherwise fire Cancel first and swallow the intended action.
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e): void => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div className="release-notes-modal">
        <h3 id="release-notes-confirm-title" className="release-notes-modal-title">
          Generate release notes for {from} → {to}?
        </h3>
        <p className="release-notes-modal-body">
          This will summarise {totalServices}{' '}
          {totalServices === 1 ? 'service' : 'services'} ({estimate.cachedCount} already cached,{' '}
          {estimate.coldCount} cold). Estimated cost:{' '}
          <strong>{formatCurrency(estimate.total, 'USD')}</strong>.
        </p>
        <div className="release-notes-modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="release-notes-modal-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="release-notes-modal-continue"
            onClick={onConfirm}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
